import { TenantESignConfig } from '@celito.clients/endpoints';

import { EntraPopupProvider } from './entra-popup.provider';
import { AppESignProvider } from './esign-provider.model';
import { OktaWidgetProvider } from './okta-widget.provider';

/**
 * WI-F7 — e-sign provider registry (ADO #38535).
 *
 * Selects the concrete provider from `TenantConfig.esign.provider`. A third
 * OIDC provider is a new `case` returning a new implementation — the modal and
 * the rest of the flow do not change.
 *
 * Returns `null` when the tenant has not onboarded e-sign (`provider === null`)
 * or config is unavailable — callers MUST handle the null (deny) path.
 *
 * Every deny below shares one principle: a misconfiguration that would make the
 * BACKEND reject 100% of signatures is caught HERE, before the user is asked to
 * re-authenticate. Anything else spends a full MFA ceremony to arrive at an
 * opaque failure.
 */

/** Options the registry needs from the tenant config beyond the `esign` block. */
export interface ResolveESignProviderOptions {
  /**
   * The SIGN-IN application (client) id — `TenantConfig.clientId`.
   *
   * {@link EntraPopupProvider} re-uses the sign-in MSAL instance, so the id
   * token it returns always carries the sign-in app's `aud`. If the tenant
   * pointed e-sign at a DIFFERENT app registration, that token can never
   * satisfy the verifier. See the deny below.
   */
  signInClientId?: string | null;
}

export const resolveESignProvider = (
  esign: TenantESignConfig | null | undefined,
  { signInClientId }: ResolveESignProviderOptions = {}
): AppESignProvider | null => {
  if (!esign) return null;

  // A tenant on `acrs` needs a Conditional Access claims challenge on the
  // token request, which this client does not implement yet. Returning a
  // provider anyway would open the popup, obtain a token with no `acrs` claim,
  // and have the backend reject it AFTER the user re-authenticated — a
  // confusing failure at signing time. Deny at selection time instead, which
  // is the path callers already handle for an un-onboarded tenant.
  if (esign.mfaMode === 'acrs') return null;

  switch (esign.provider) {
    case 'okta':
      // The widget needs both to reach Okta at all; without them it renders
      // against `baseUrl: ''` and fails with an opaque widget error.
      if (!esign.issuer || !esign.clientId) return null;
      return new OktaWidgetProvider({
        issuer: esign.issuer,
        clientId: esign.clientId,
      });
    case 'entra':
      // The popup mints a token from the SIGN-IN app registration. A tenant that
      // configured e-sign against a different app would have every signature
      // rejected on `aud` (and possibly `iss`) with nothing on screen to explain
      // it — this client has no second PCA to satisfy that configuration.
      if (
        esign.clientId &&
        signInClientId &&
        esign.clientId !== signInClientId
      ) {
        return null;
      }
      return new EntraPopupProvider();
    case null:
    default:
      return null;
  }
};
